import {
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import tokens from '@verifime/design-tokens';
import { Dispatch, SetStateAction } from 'react';

type TVariant = 'INDIVIDUAL' | 'ORGANISATION';
type TVerifyEntityType = 'INDIVIDUAL' | 'CURRENT_ORGANISATION' | 'SETUP_ORGANISATION';

const IndividualPrompt = ({
  initialVerifyEntityType,
  setVerifyEntityType,
  serviceProviderName,
}: Readonly<{
  initialVerifyEntityType: TVerifyEntityType;
  setVerifyEntityType: Dispatch<SetStateAction<TVerifyEntityType>>;
  serviceProviderName: string;
}>) => {
  return (
    <Paper sx={{ padding: tokens.spacingSm }}>
      <Stack sx={{ marginBottom: tokens.spacingXs }}>
        <Typography variant="caption" color="textSecondary">
          IMPORTANT - BEFORE YOU SUBMIT
        </Typography>
        <Typography variant="h6">
          Does {serviceProviderName} need you to verify your business or trust?
        </Typography>
      </Stack>
      <FormControl sx={{ paddingLeft: tokens.spacingSm }}>
        <RadioGroup
          name="controlled-radio-buttons-group"
          value={initialVerifyEntityType}
          onChange={(e) => setVerifyEntityType(e.target.value as TVerifyEntityType)}
        >
          <FormControlLabel
            value="INDIVIDUAL"
            control={<Radio />}
            label={
              <Typography>
                <Typography component="span" fontWeight="bold">
                  No
                </Typography>{' '}
                - I am transacting as an individual
              </Typography>
            }
          />
          <FormControlLabel
            value="SETUP_ORGANISATION"
            control={<Radio />}
            label={
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Yes
                </Typography>{' '}
                - I am transacting on behalf of a business/trust entity
              </Typography>
            }
          />
        </RadioGroup>
      </FormControl>
      <Typography variant="body2" color="textSecondary">
        e.g. Sole Trader, Partnership, Company, SMSF, Trust etc.
      </Typography>
    </Paper>
  );
};

const OrganisationPrompt = ({
  initialVerifyEntityType,
  setVerifyEntityType,
}: Readonly<{
  initialVerifyEntityType: TVerifyEntityType;
  setVerifyEntityType: Dispatch<SetStateAction<TVerifyEntityType>>;
}>) => {
  return (
    <Paper sx={{ padding: tokens.spacingSm }}>
      <Stack sx={{ marginBottom: tokens.spacingXs }}>
        <Typography variant="caption" color="textSecondary">
          IMPORTANT - BEFORE YOU SUBMIT
        </Typography>

        <Typography variant="h6">Do you need to verify additional entities?</Typography>
      </Stack>
      <FormControl sx={{ paddingLeft: tokens.spacingSm }}>
        <RadioGroup
          name="controlled-radio-buttons-group"
          value={initialVerifyEntityType}
          onChange={(e) => setVerifyEntityType(e.target.value as TVerifyEntityType)}
        >
          <FormControlLabel
            value="CURRENT_ORGANISATION"
            control={<Radio />}
            label={
              <Typography>
                <Typography component="span" fontWeight="bold">
                  No
                </Typography>{' '}
                - complete my verification
              </Typography>
            }
          />
          <FormControlLabel
            value="SETUP_ORGANISATION"
            control={<Radio />}
            label={
              <Typography>
                <Typography component="span" fontWeight="bold">
                  Yes
                </Typography>{' '}
                - I need to verify another entity e.g. company, trust, SMSF, partnership, or other
                entity
              </Typography>
            }
          />
        </RadioGroup>
      </FormControl>
    </Paper>
  );
};

export default function AdditionalEntityVerificationPrompt({
  initialVerifyEntityType,
  setVerifyEntityType,
  serviceProviderName,
  variant,
}: Readonly<{
  initialVerifyEntityType: TVerifyEntityType;
  setVerifyEntityType: Dispatch<SetStateAction<TVerifyEntityType>>;
  serviceProviderName: string;
  variant: TVariant;
}>) {
  if (variant === 'INDIVIDUAL') {
    return (
      <IndividualPrompt
        initialVerifyEntityType={initialVerifyEntityType}
        setVerifyEntityType={setVerifyEntityType}
        serviceProviderName={serviceProviderName}
      />
    );
  }
  if (variant === 'ORGANISATION') {
    return (
      <OrganisationPrompt
        initialVerifyEntityType={initialVerifyEntityType}
        setVerifyEntityType={setVerifyEntityType}
      />
    );
  }
  return null;
}
